import translate from '../../../i18n'
import { OrderHistory, OrderHistoryRefundRequest } from '../share-type'
import { Alert, Button, Checkbox, Descriptions, Form, InputNumber, Modal } from 'antd'

type RefundMode = 'partial' | 'manual'

interface IProps {
  mode: RefundMode
  onCancel: () => void
  onFinish: (request: OrderHistoryRefundRequest) => Promise<void>
  order: OrderHistory
}

const OrderRefundModal = ({ mode, onCancel, onFinish, order }: IProps) => {
  const [form] = Form.useForm<OrderHistoryRefundRequest>()
  const userCredits = order.userCredits ?? 0
  const purchasedCredits = order.purchasedCredits ?? 0
  const maxReclaimableCredits = order.refundableCredits ?? Math.min(userCredits, purchasedCredits)
  const maximumRefundAmountInCents = order.maximumRefundAmountInCents ?? order.totalFee ?? 0

  const handleSubmit = (request: OrderHistoryRefundRequest) => {
    onFinish(request).then(onCancel)
  }

  return (
    <Modal
      open
      footer={null}
      destroyOnHidden
      title={translate(
        mode === 'manual' ? 'orderHistories.manualRefund' : 'orderHistories.partialRefund',
      )}
      onCancel={onCancel}
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item label={translate('orderHistories.userTotalCredits')}>
          {userCredits}
        </Descriptions.Item>
        <Descriptions.Item label={translate('orderHistories.orderPurchasedCredits')}>
          {purchasedCredits}
        </Descriptions.Item>
        <Descriptions.Item label={translate('orderHistories.maximumRefundCredits')}>
          {maxReclaimableCredits}
        </Descriptions.Item>
        <Descriptions.Item label={translate('orderHistories.orderTotalFee')}>
          ¥{(order.totalFee ?? 0) / 100}
        </Descriptions.Item>
        <Descriptions.Item label={translate('orderHistories.maximumRefundAmount')}>
          ¥{maximumRefundAmountInCents / 100}
        </Descriptions.Item>
      </Descriptions>
      <Alert
        showIcon
        type="warning"
        message={translate('orderHistories.refundWarning')}
        style={{ marginBottom: 16 }}
      />
      <Form<OrderHistoryRefundRequest>
        form={form}
        initialValues={{ restoreCoupon: false }}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="refundAmountInCents"
          label={translate('orderHistories.refundAmountInCents')}
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={maximumRefundAmountInCents} />
        </Form.Item>
        <Form.Item
          name="refundCredits"
          label={translate('orderHistories.reclaimCredits')}
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={maxReclaimableCredits} />
        </Form.Item>
        <Form.Item
          name="restoreCoupon"
          valuePropName="checked"
          wrapperCol={{ offset: 7, span: 17 }}
        >
          <Checkbox>{translate('orderHistories.restoreCoupon')}</Checkbox>
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 7, span: 17 }}>
          <Button type="primary" danger htmlType="submit">
            {translate(
              mode === 'manual'
                ? 'orderHistories.confirmManualRefund'
                : 'orderHistories.startWechatPartialRefund',
            )}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default OrderRefundModal
